import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollToContact, scrollToSection } from '../hooks/useScrollToContact';
import Picture from './Picture';

const NAV_ITEMS: [id: string, label: string][] = [
  ['rundflug', 'Rundflüge'],
  ['ueber-uns', 'Über uns'],
  ['impressionen', 'Impressionen'],
  ['pilot-werden', 'Pilot werden'],
  ['ausbildung', 'Ausbildung'],
  ['flugzeuge', 'Flugzeuge'],
  ['flugplatz', 'Flugplatz'],
  ['veranstaltungen', 'Veranstaltungen'],
  ['kontakt', 'Kontakt'],
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const scrollToContact = useScrollToContact();
  const isHome = location.pathname === '/';

  // Auf der Startseite sanft scrollen; auf Unterseiten führt /#id normal zur Startseite
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    setIsMenuOpen(false);
    if (!isHome) return;
    if (id === 'kontakt') {
      scrollToContact(e);
    } else if (document.getElementById(id)) {
      e.preventDefault();
      scrollToSection(id);
    }
  };

  // Logo: auf der Startseite nach oben scrollen, sonst zur Startseite
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setIsMenuOpen(false);
    if (!isHome) return;
    e.preventDefault();
    window.history.replaceState(null, '', '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white">
      <div className="mx-auto flex h-16 max-w-screen-xl items-center gap-3 px-5 lg:h-[72px] lg:gap-1 lg:px-6 xl:px-12">
        <a href="/" onClick={handleLogoClick} className="mr-auto flex flex-none items-center gap-3 text-gray-900 hover:text-gray-900">
          <Picture
            src="/images/logo.png"
            alt="Flugsportverein Otto Lilienthal Bad Langensalza"
            sizes="48px"
            loading="eager"
            className="h-11 w-auto lg:h-12"
          />
          <span className="flex flex-col text-sm leading-[1.15]">
            <span className="font-extrabold">Flugsportverein</span>
            <span className="font-semibold">Otto Lilienthal</span>
          </span>
        </a>

        <nav aria-label="Hauptnavigation" className="hidden lg:flex lg:items-center">
          {NAV_ITEMS.map(([id, label]) => (
            <a
              key={id}
              href={`/#${id}`}
              onClick={(e) => handleNavClick(e, id)}
              className="flex-none whitespace-nowrap border-b-2 border-transparent px-1.5 py-2 text-[13px] font-medium text-gray-900 transition-colors hover:border-blue-600 hover:text-blue-600 xl:px-2 xl:text-sm"
            >
              {label}
            </a>
          ))}
        </nav>

        <a
          href="/#rundflug"
          onClick={(e) => handleNavClick(e, 'rundflug')}
          className="ml-3 hidden min-h-[44px] flex-none items-center bg-blue-600 px-[18px] text-sm font-semibold text-white transition-colors hover:bg-blue-700 hover:text-white xl:inline-flex"
        >
          Rundflug buchen
        </a>

        <button
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          className="grid h-11 w-11 flex-none place-items-center text-gray-900 lg:hidden"
        >
          {isMenuOpen ? (
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobiles Menü liegt über dem Inhalt, damit sich Scroll-Ziele beim Schließen nicht verschieben */}
      <nav
        id="mobile-menu"
        aria-label="Hauptnavigation mobil"
        hidden={!isMenuOpen}
        className="absolute inset-x-0 top-full max-h-[calc(100vh-4rem)] overflow-y-auto border-b border-t border-gray-200 bg-white px-2 pb-3 pt-2 shadow-lg lg:hidden"
      >
        {NAV_ITEMS.map(([id, label]) => (
          <a
            key={id}
            href={`/#${id}`}
            onClick={(e) => handleNavClick(e, id)}
            className="block p-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-blue-600"
          >
            {label}
          </a>
        ))}
      </nav>
    </header>
  );
};

export default Header;
